import { useSearchParams } from 'react-router-dom';
import { GroupId, Pagination } from 'types';
import { useSearchFilter } from 'hooks/useSearchFilter/useSearchFilter';
import { childFilterFields } from 'consts/filters/child/childFilterFields';
import { DEFAULT_PAGE, PAGE_PARAM_KEY } from 'consts/pagination';
import { useDateFilter } from 'hooks/useDateFilter/useDateFilter';
import { DATE_FILTER_KEY } from 'consts/filters/common/filtersKeys';
import { ChosenFilter, FilterKeys } from '../ChildrenSettlementPage.types';

type ChildrenSettlementFiltersProps = {
    currentPage: Pagination['currentPage'];
};

export const useChildrenSettlementPageFilters = ({
    currentPage,
}: ChildrenSettlementFiltersProps) => {
    const { searchFilterDebounceValue, handleSearchInput } = useSearchFilter({
        currentPage,
    });

    const [searchParams, setSearchParams] = useSearchParams();

    const groupsParams = searchParams.getAll(childFilterFields.groupIds);

    const handleClearFilter = (filterKey: FilterKeys) => {
        setSearchParams((prevSearchParams) => {
            prevSearchParams.delete(filterKey);
            prevSearchParams.set(PAGE_PARAM_KEY, DEFAULT_PAGE);
            return prevSearchParams;
        });
    };

    const updateFilter = (filterKey: FilterKeys, value: string[]) => {
        setSearchParams(
            (prevSearchParams) => {
                prevSearchParams.delete(filterKey);
                value.forEach((valueItem) => prevSearchParams.append(filterKey, valueItem));

                prevSearchParams.set(PAGE_PARAM_KEY, DEFAULT_PAGE);
                return prevSearchParams;
            },
            { replace: currentPage === DEFAULT_PAGE },
        );
    };

    const handleChangeFilter = (filterKey: FilterKeys, value: string[] | undefined) => {
        if (!value || !value.length) {
            handleClearFilter(filterKey);
            return;
        }
        updateFilter(filterKey, value);
    };

    const { dateFilterValue, handleChangeDateFilter, handleClickNextOrPrevDate } = useDateFilter({
        filterKey: DATE_FILTER_KEY,
        currentPage,
    });

    const updateChosenFilter = (filter: ChosenFilter) => {
        switch (filter.type) {
            case 'search':
                handleSearchInput(filter.value);
                break;

            case 'groupIds':
                handleChangeFilter('groupIds', filter.value);
                break;

            case 'date':
                handleChangeDateFilter(filter.value);
                break;

            case 'dateNextPrev':
                handleClickNextOrPrevDate(filter.value.direction, filter.value.unit);
                break;
            default:
                break;
        }
    };

    return {
        filtersValues: {
            dateFilterValue,
            searchFilterDebounceValue,
            groupsFilterValue: groupsParams ? (groupsParams as GroupId[]) : null,
        },
        updateChosenFilter,
    };
};
